import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import VerticalAlignTopOutlinedIcon from '@mui/icons-material/VerticalAlignTopOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import { api } from '../../app/http-common';
import { buildReorderBatches, canReorderPast, rowOrderZone } from './reorderGroups';
import { useCanEdit, useIsAdmin } from '../../hooks/useFrontegg';
import useBucketData from './hooks/useBucketData';
import useDragAutoScroll from './hooks/useDragAutoScroll';
import {
  ALL_STATUS_ORDER,
  COMPLETED_FILTER_STATUSES,
  NEWSLETTER_MAX_STORIES,
  STATUS_LABELS,
  STATUS_FORWARD_MAP,
  bucketConfigUrl,
} from './constants';
import StoryRow from './components/StoryRow';
import TargetBucketsPanel from './components/TargetBucketsPanel';
import MoveToModal from './components/MoveToModal';
import DragIntentModal from './components/DragIntentModal';
import DuplicateModal from './components/DuplicateModal';
import StaleArchiveModal from './components/StaleArchiveModal';
import ArchiveOldMenu from './components/ArchiveOldMenu';
import UndoToast from './components/UndoToast';
import useExclusionConfirm from '../CreateBrief/useExclusionConfirm';
import ExclusionWarningModal from '../CreateBrief/ExclusionWarningModal';
import CreateNewsletterModal from './components/CreateNewsletterModal';
import NewsletterSettingsModal from './components/NewsletterSettingsModal';
import BucketsLoading from './components/BucketsLoading';
import { SLOTS, SURFACES, saveReturn, peekReturn, consumeReturn, restoreScroll } from '../../app/navContext';
import AppModal from '../../components/AppModal/AppModal';
import './BucketManagerNew.css';

// Spec 6.2 — primary filters. Active suggestions is the default.
//
// DELIBERATE ROLLBACK of Adi #10, which gave every workflow status its own tab
// (Accepted / In work / Sent to publisher / Rejected) and folded the archive
// control in as a ninth. Per the Middle East Review PRD §A6, the set returns to
// Suggestions / Completed / All: the per-status tabs added complexity without
// adding any information All does not already show, because All renders the
// same rows grouped into status blocks.
//
// Archive is a FLAG, not a status, so it is once again an independent control.
// Making it a tab meant "Accepted, including archived" was unaskable — picking
// the archive tab discarded the status filter and vice versa. The two questions
// are orthogonal and are asked separately again.
const PRIMARY_FILTERS = {
  active: { label: 'Suggestions', statuses: ['suggestion'] },
  completed: { label: 'Completed', statuses: COMPLETED_FILTER_STATUSES },
  all: { label: 'All', statuses: null },
};

// Independent of the tabs above: any of the three may combine with any tab.
const ARCHIVE_OPTIONS = [
  { value: 'hide', label: 'Hide archived' },
  { value: 'include', label: 'Include archived' },
  { value: 'only', label: 'Only archived' },
];

export default function BucketStories() {
  const canEdit = useCanEdit();
  // Newsletters are admin-only in v1 (answer D1); the screen itself is editor-gated.
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { bucketId } = useParams();
  const { clients, rawBuckets, reload: reloadBuckets, loading: bucketsLoading } = useBucketData();

  // §1.1 / §1.10 / §2.1 — the pending return context, read ONCE before any state
  // is initialised.
  //
  // Two distinct things come out of it:
  //   - `restore`: state to seed this screen with, when we are coming BACK here
  //     from a story. Seeding the initial state (rather than applying it in an
  //     effect) matters — an effect would fetch once with the default filters and
  //     again with the restored ones.
  //   - `returnCtx`: where the header's back control should go. Peeked, never
  //     consumed here, or the control would vanish on the next render.
  const [entry] = useState(() => {
    // STORY slot: state we parked when leaving for a story, if it was this bucket.
    const story = peekReturn(SURFACES.BUCKET_STORIES, SLOTS.STORY);
    return {
      restore: story?.state?.bucketId === bucketId ? story.state : null,
      // SCREEN slot: where the back control should go.
      returnCtx: peekReturn(null, SLOTS.SCREEN),
    };
  });
  const { restore, returnCtx } = entry;

  const [primary, setPrimary] = useState(() => {
    // A restored value may name a tab that no longer exists (the per-status tabs
    // removed in the §A6 rollback are still sitting in saved navigation state and
    // in any open browser tab). Fall back rather than crash on
    // PRIMARY_FILTERS[undefined].statuses.
    const saved = restore?.primary;
    return saved && PRIMARY_FILTERS[saved] ? saved : 'active';
  });
  // §A6 — independent of `primary`. 'archived' used to be a tab; a restored
  // session may still carry it, and it maps to 'only'.
  const [archiveFilter, setArchiveFilter] = useState(() => {
    const saved = restore?.archiveFilter ?? (restore?.primary === 'archived' ? 'only' : null);
    return ARCHIVE_OPTIONS.some((o) => o.value === saved) ? saved : 'hide';
  });

  const [storySearch, setStorySearch] = useState(() => restore?.storySearch || '');
  const [stories, setStories] = useState({ items: [], statusCounts: {}, total: 0 });
  // The rows as they stand RIGHT NOW, including optimistic drag steps React has
  // not re-rendered yet. A drag fires several hover events per frame; reading
  // state alone made each one recompute from the same stale array.
  const itemsRef = useRef(stories.items);
  const reorderDirtyRef = useRef(false);
  // Resync from state on every render EXCEPT mid-drag: an unrelated re-render
  // between an optimistic step and its commit would otherwise put the pre-drag
  // array back under the drag.
  if (!reorderDirtyRef.current) itemsRef.current = stories.items;
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set(restore?.selected || []));
  // Follow-up §7 — the expanded row lives HERE, not inside StoryRow, so Back
  // from Item Details can reopen the same inline review. Seeded from the return
  // context for the same reason the filters are.
  const [expandedStoryId, setExpandedStoryId] = useState(() => restore?.expandedStoryId || null);
  // §7.3 — the row's offset from the top of the scroll viewport when we left.
  // Restoring raw scrollTop is not enough: rows above may render at a different
  // height (sources load, statuses change), so we re-anchor on the row itself.
  const pendingAnchorRef = useRef(
    restore?.expandedStoryId
      ? { storyId: restore.expandedStoryId, offset: restore.expandedRowOffset ?? 0 }
      : null,
  );
  const [selectAllMatching, setSelectAllMatching] = useState(() => !!restore?.selectAllMatching);
  const [toast, setToast] = useState(null);
  // One dialog for every transfer out of this screen. The hook owns the
  // server round trip and the two steps; see useExclusionConfirm.
  const exclusion = useExclusionConfirm();
  const [moveModal, setMoveModal] = useState(null);
  const [dragIntent, setDragIntent] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [comment, setComment] = useState(null);
  const [staleArchive, setStaleArchive] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(false);
  const [newsletterFlow, setNewsletterFlow] = useState(null);
  const [newsletterSettings, setNewsletterSettings] = useState(false);
  // §C3 — set when the bucket holds more suggestions than the analysis cap.
  const [mediaAnalysisOverflow, setMediaAnalysisOverflow] = useState(null);
  const [newsletterJob, setNewsletterJob] = useState(null);
  const [contact, setContact] = useState({ canSend: false, reason: 'Checking recipient…', recipients: [] });
  const [targetsCollapsed, setTargetsCollapsed] = useState(() => !!restore?.targetsCollapsed);
  const [expandedTargets, setExpandedTargets] = useState(() => new Set(restore?.expandedTargets || []));
  const undoRef = useRef(null);
  // §1.1 — the scroll container, so the position can be restored on return.
  const scrollRef = useRef(null);
  const pendingScrollRef = useRef(restore?.scrollTop ?? null);
  const scrollDeadlineRef = useRef(restore?.scrollTop ? Date.now() + 8000 : 0);

  useEffect(() => {
    if (!canEdit) navigate('/', { replace: true });
  }, [canEdit, navigate]);

  // Adi #3 — reordering used to stall at the fold; the list now scrolls when a
  // dragged story nears its top or bottom edge.
  useDragAutoScroll(useCallback(() => scrollRef.current, []));

  const bucket = useMemo(
    () => rawBuckets.find((b) => b._id === bucketId) || null,
    [rawBuckets, bucketId],
  );
  const client = useMemo(
    () => clients.find((c) => c.buckets.some((b) => b._id === bucketId)) || null,
    [clients, bucketId],
  );

  // Defensive for the same reason the persisted value is validated above: an
  // unknown key here throws during render, which blanks the whole page rather
  // than showing a wrong tab.
  const statusParam = (PRIMARY_FILTERS[primary] || PRIMARY_FILTERS.active).statuses;

  const loadStories = useCallback(async ({ background = false } = {}) => {
    if (!bucketId) return;
    // §1.8 — background refetches don't swap the list for the loader. That
    // "reload" the delta complained about was really a loading-state flash on
    // every action. First mount is not background; a subsequent action-driven
    // refresh is, so the rows update in place.
    if (!background) setLoading(true);
    try {
      const res = await api.getBucketStories(bucketId, {
        status: statusParam ? statusParam.join(',') : undefined,
        query: storySearch.trim() || undefined,
        include_archived: archiveFilter,
        limit: 200,
      });
      setStories({ items: res.items || [], statusCounts: res.statusCounts || {}, total: res.total || 0 });
      // §7.3 — re-anchor the previously expanded row to the same viewport
      // offset. Done here (not in an effect) for the same reason as the scroll
      // restore below: this is the one place that knows rows just arrived.
      // Two frames: the first lets the row mount, the second lets its inline
      // sources region take its final height.
      if (pendingAnchorRef.current && (res.items || []).length) {
        const { storyId, offset } = pendingAnchorRef.current;
        pendingAnchorRef.current = null;
        pendingScrollRef.current = null; // the anchor supersedes raw scrollTop
        const align = () => {
          const el = scrollRef.current;
          if (!el) return;
          const row = el.querySelector(`[data-bucket-story-id="${storyId}"]`);
          if (!row) return;
          const delta = (row.getBoundingClientRect().top - el.getBoundingClientRect().top) - offset;
          el.scrollTop += delta;
        };
        requestAnimationFrame(() => { align(); requestAnimationFrame(align); });
      }
      // §1.1 — restore from here rather than an effect; see the note in
      // InProduction. This is the one place that knows rows just arrived.
      if (pendingScrollRef.current && (res.items || []).length) {
        if (Date.now() < scrollDeadlineRef.current) {
          restoreScroll(() => scrollRef.current, pendingScrollRef.current);
        } else {
          pendingScrollRef.current = null;
        }
      }
    } catch {
      setStories({ items: [], statusCounts: {}, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [bucketId, statusParam, storySearch, archiveFilter]);

  // Spec 6.1 — fetch current data when the workspace opens, when the source
  // bucket changes, and when the administrator returns to the tab.
  // Skips exactly one selection-clear: the first run after a restore, which
  // would otherwise discard the checked rows we just seeded.
  const keepRestoredSelectionRef = useRef(!!restore);

  useEffect(() => {
    loadStories();
    if (keepRestoredSelectionRef.current) {
      keepRestoredSelectionRef.current = false;
      return;
    }
    setSelected(new Set());
    setSelectAllMatching(false);
  }, [loadStories]);

  // The restore payload has been applied to state, so drop it. Peeking left it
  // in place for this render; leaving it would make a later plain visit here
  // silently inherit stale filters.
  useEffect(() => {
    if (restore) consumeReturn(SURFACES.BUCKET_STORIES, SLOTS.STORY);
  }, [restore]);

  // §1.1 — put the scroll position back once the restored list has painted.

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadStories({ background: true }); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadStories]);

  // Follow-up §3.4 — Send link recognises EVERY email endpoint saved against
  // this bucket, not just a single primary contact.
  //
  // The reported defect (§3.1C): an admin adds an Email endpoint in Bucket
  // Settings and Send link still says no recipient is defined. Two causes —
  // adding an endpoint creates a client-scoped ClientContact whose id only
  // reaches the bucket if the admin then saves, and the old gate read
  // resolveContactPoint, which walks out to the client/publisher and reports
  // source:'client' for exactly that case. That is not bucket-owned, so the
  // control stayed disabled.
  //
  // /recipients returns only primaryContactId + recipientContactIds filtered to
  // type 'email', so a publisher address can never enable the control (§3.3)
  // and a WhatsApp-only endpoint cannot masquerade as an email recipient.
  useEffect(() => {
    let cancelled = false;
    if (!bucketId) return undefined;
    (async () => {
      try {
        const res = await api.getBucketRecipients(bucketId, { silent: true });
        if (cancelled) return;
        const list = Array.isArray(res?.recipients) ? res.recipients : [];
        if (list.length > 0) {
          setContact({ canSend: true, reason: '', recipients: list });
          return;
        }
        // Nothing bucket-owned. Distinguish "publisher has one, we won't use it"
        // from "nothing anywhere" so the explanation stays actionable.
        let reason = 'This bucket has no email recipient. Add one in Configure bucket → Settings.';
        try {
          const cp = await api.getBucketContactPoint(bucketId, { silent: true });
          if (!cancelled && cp?.type && cp.type !== 'none' && cp.value) {
            reason = 'This bucket has no recipient of its own. Publisher-level contacts are not used for a single-bucket send — set a bucket recipient in Configure bucket.';
          }
        } catch { /* the primary answer above already stands */ }
        if (!cancelled) setContact({ canSend: false, reason, recipients: [] });
      } catch {
        if (!cancelled) {
          setContact({
            canSend: false,
            reason: 'This bucket has no configured recipient.',
            recipients: [],
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [bucketId]);

  // §1.1 — everything the delta lists as restorable on return.
  const captureState = useCallback(() => ({
    bucketId,
    primary,
    archiveFilter,
    storySearch,
    selected: [...selected],
    selectAllMatching,
    scrollTop: scrollRef.current ? scrollRef.current.scrollTop : 0,
    targetsCollapsed,
    expandedTargets: [...expandedTargets],
    // §7.3 — which review was open, and where that row sat in the viewport.
    expandedStoryId,
    expandedRowOffset: (() => {
      if (!expandedStoryId || !scrollRef.current) return 0;
      const row = scrollRef.current.querySelector(`[data-bucket-story-id="${expandedStoryId}"]`);
      if (!row) return 0;
      return row.getBoundingClientRect().top - scrollRef.current.getBoundingClientRect().top;
    })(),
  }), [bucketId, primary, archiveFilter, storySearch, selected, selectAllMatching,
       targetsCollapsed, expandedTargets, expandedStoryId]);

  // §1.1 — open a story's full details, recording where to come back to.
  const openFullStory = useCallback((item) => {
    const slug = item?.story?.slug;
    if (!slug) return;
    saveReturn({
      surface: SURFACES.BUCKET_STORIES,
      url: `/publisher-buckets-new/bucket/${bucketId}`,
      label: 'Back to Bucket Stories',
      state: captureState(),
    }, SLOTS.STORY);
    // Carry the originating bucket. The article screen derives the restricted
    // production context from (bucket, story) — which articles may be a source,
    // whether the method is locked, what the scope is. Opening the story without
    // it meant a story reached from a matching-only bucket looked unrestricted:
    // every article selected, no scope banner, and the claims step still
    // offered. §1 also requires bucket-initiated and feed-initiated production
    // to stay distinguishable, which is why this is explicit rather than
    // inferred from whatever filter the feed happens to hold.
    navigate(`/story/${slug}?bucket=${encodeURIComponent(bucketId)}`);
  }, [bucketId, captureState, navigate]);

  // The header back control. Where it goes depends on how this screen was
  // entered (§1.10 / §2.1); with no recorded entry it falls back to the bucket
  // list, which is the existing behaviour.
  const handleBack = useCallback(() => {
    // PEEK, do not consume. The entry answers two questions: where this button
    // goes, and what the destination should restore itself to. Consuming here
    // read the first and destroyed the second, so the return navigated
    // correctly and then landed on a completely default screen.
    //
    // The destination consumes it once it has applied the restore.
    const ctx = peekReturn(null, SLOTS.SCREEN);
    navigate(ctx?.url || '/publisher-buckets-new');
  }, [navigate]);

  const backLabel = returnCtx?.label || 'All buckets';

  // §1.8 — action-driven refresh is silent; only the affected controls show
  // progress. First-mount load (in the useEffect below) stays as the loader path.
  // PRD item 5 — suggestions the pre-snapshot language check removed. Shown
  // here, on Bucket Stories, and deliberately not on an admin settings page:
  // the person who needs to know a suggestion vanished is the editor looking
  // at the list.
  const [languageNotice, setLanguageNotice] = useState(null);
  const loadLanguageNotice = useCallback(async () => {
    if (!bucketId) return;
    try {
      const res = await api.getBucketLanguageRemovals(bucketId, {}, { silent: true });
      setLanguageNotice(res?.notice || null);
    } catch {
      // A missing notice must never break the list it sits above.
      setLanguageNotice(null);
    }
  }, [bucketId]);

  useEffect(() => { loadLanguageNotice(); }, [loadLanguageNotice]);

  const refreshAll = useCallback(() => {
    loadStories({ background: true });
    reloadBuckets();
    // A snapshot is what triggers the check, and every caller of refreshAll
    // has just taken one or changed the list the next one will capture.
    loadLanguageNotice();
  }, [loadStories, reloadBuckets, loadLanguageNotice]);

  const counts = stories.statusCounts;
  const allCount = Object.entries(counts)
    .filter(([s]) => s !== 'deleted')
    .reduce((a, [, v]) => a + v, 0);
  // One count per tab, from the same statusCounts the server already returns.
  // There is no archive count: statusCounts is keyed by status, and archived is
  // a flag orthogonal to it — a stale or wrong number is worse than none.
  const tabCount = useCallback((key) => {
    if (key === 'all') return allCount;
    const st = (PRIMARY_FILTERS[key] || PRIMARY_FILTERS.active).statuses || [];
    return st.reduce((a, x) => a + (counts[x] || 0), 0);
  }, [counts, allCount]);

  // Spec 6.3 — the All view is one continuous list split into status blocks in
  // workflow order. Ordering is internal to each status.
  const grouped = useMemo(() => {
    if (primary !== 'all') return [{ status: null, items: stories.items }];
    const byStatus = new Map();
    for (const item of stories.items) {
      if (!byStatus.has(item.status)) byStatus.set(item.status, []);
      byStatus.get(item.status).push(item);
    }
    const blocks = [];
    for (const status of ALL_STATUS_ORDER) {
      if (byStatus.has(status)) blocks.push({ status, items: byStatus.get(status) });
    }
    for (const [status, items] of byStatus) {
      if (!ALL_STATUS_ORDER.includes(status)) blocks.push({ status, items });
    }
    return blocks;
  }, [primary, stories.items]);

  // The refs `toggleCheck` reads. Kept in sync with what is painted, so a
  // Shift-click range covers exactly the visible order.
  useEffect(() => {
    displayedItemsRef.current = grouped.flatMap((block) => block.items);
  }, [grouped]);

  // ── Selection (spec 6.7) + §1.4 Shift-click range ──
  const lastCheckedIdRef = useRef(null);

  // Range selection needs the currently-displayed order — the same list that is
  // painted, in the same sequence. The `grouped` memo below is the canonical
  // one; expose it as a ref so toggleCheck can read it without a dep loop.
  const displayedItemsRef = useRef([]);

  const toggleCheck = (id, event) => {
    setSelectAllMatching(false);
    const shift = event?.nativeEvent?.shiftKey || event?.shiftKey;
    const list = displayedItemsRef.current;
    const anchorId = lastCheckedIdRef.current;
    setSelected((prev) => {
      const next = new Set(prev);
      // Shift-click on a second checkbox toggles the whole range between the
      // last-clicked one and this one INTO the current selection. If nothing was
      // clicked yet, or the anchor is no longer displayed, this falls back to a
      // plain toggle so the click is never silently swallowed.
      const anchorIdx = anchorId ? list.findIndex((it) => it._id === anchorId) : -1;
      const thisIdx = list.findIndex((it) => it._id === id);
      if (shift && anchorIdx >= 0 && thisIdx >= 0 && anchorIdx !== thisIdx) {
        const [lo, hi] = anchorIdx < thisIdx ? [anchorIdx, thisIdx] : [thisIdx, anchorIdx];
        for (let i = lo; i <= hi; i += 1) next.add(list[i]._id);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastCheckedIdRef.current = id;
  };
  const toggleSelectAll = (e) => {
    if (e.target.checked) setSelected(new Set(stories.items.map((i) => i._id)));
    else { setSelected(new Set()); setSelectAllMatching(false); }
  };

  const selectedItems = stories.items.filter((i) => selected.has(i._id));
  const selectionCount = selectAllMatching ? stories.total : selected.size;
  const allSelectedAreSuggestions =
    selectedItems.length > 0 && selectedItems.every((i) => i.status === 'suggestion');
  // With a whole-bucket scope we can only be sure every row is a Suggestion when
  // the primary filter itself is Active suggestions.
  const canBulkMove = selectAllMatching ? primary === 'active' : allSelectedAreSuggestions;

  // The server resolves "every record matching the filters" (spec 6.7).
  const bulkSelection = useCallback(() => (selectAllMatching
    ? {
      scope: 'bucket',
      status: statusParam ? statusParam.join(',') : undefined,
      query: storySearch.trim() || undefined,
      includeArchived: archiveFilter,
    }
    : { ids: [...selected] }),
  [selectAllMatching, statusParam, storySearch, archiveFilter, selected]);

  const clearSelection = () => { setSelected(new Set()); setSelectAllMatching(false); };

  // ── Reorder within a status (spec 6.3, §A1) ──
  // The swap is applied locally on hover so the list tracks the pointer, then
  // persisted once on drop.
  //
  // STATUS is the only boundary. Zones used to be one too, and refusing a
  // cross-zone drag was the reported bug: zones are invisible, so an editor
  // dragging past yesterday's fill saw the row highlight, accept the drop, and
  // then not move. A cross-zone drag now re-zones the row, which is how
  // `Move to top` has always resolved the same conflict.
  /**
   * Apply one hover step of a drag, and say whether it was applied.
   *
   * Returns a boolean because the caller (StoryRow's `hover`) has to keep the
   * drag's idea of its own index in step with where the row actually is. It
   * used to advance that index unconditionally, so a single refused step left
   * the drag pointing at the wrong row and the NEXT, legal, step moved a story
   * the editor never touched.
   *
   * Reads and writes `itemsRef` rather than only `prev`, because several hover
   * events can fire before React re-renders; going through state alone made
   * each of them recompute from the same stale array.
   */
  const handleReorder = useCallback((fromIndex, toIndex) => {
    const items = itemsRef.current;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
      return false;
    }
    if (!canReorderPast(items[fromIndex], items[toIndex])) return false;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    // A drag across a zone boundary re-zones the row instead of being refused.
    // The zone is invisible on screen, so refusing read as "reordering does not
    // work"; `Move to top` already resolves the same conflict this way.
    const destZone = rowOrderZone(items[toIndex]);
    next.splice(toIndex, 0, rowOrderZone(moved) === destZone ? moved : { ...moved, orderZone: destZone });

    itemsRef.current = next;
    reorderDirtyRef.current = true;
    setStories((prev) => ({ ...prev, items: next }));
    return true;
  }, []);

  const handleReorderCommit = useCallback(async () => {
    if (!reorderDirtyRef.current) return;
    reorderDirtyRef.current = false;
    // Persist one batch per (status, zone), each carrying the zone to write.
    // Posting the whole list grouped by status alone meant every bucket holding
    // more than one zone had its drags refused in full, and the refusal arrived
    // as HTTP 200 so nothing here noticed.
    //
    // Read from the ref, not from `stories.items`: the last hover step may not
    // have re-rendered before the drop fired, and persisting the pre-drop array
    // would quietly save a different order than the one on screen.
    try {
      for (const { ids, orderZone } of buildReorderBatches(itemsRef.current)) {
        await api.reorderBucketStories(bucketId, ids, { orderZone, silent: true });
      }
      reloadBuckets();
    } catch {
      // The screen is now showing an order the server did not accept. Say so
      // and reload, rather than leaving the drag standing over stale data.
      setToast({ message: 'Could not save the new order.', type: 'error' });
      loadStories();
    }
  }, [bucketId, reloadBuckets, loadStories]);

  // ── Single-story actions ──
  const handleAdvanceStatus = useCallback(async (item) => {
    const prevStatus = item.status;
    try {
      // Spec 6.4 — a publisher-written bucket resolves an accepted Suggestion to
      // "Handled by publisher" instead of entering the production cycle.
      const res = await api.advanceBucketStoryStatus(item._id, { silent: true }, { respectWritingResponsibility: true });
      // Adi #6 — advancing a status used to make the row vanish with no
      // explanation: it had simply left the active filter. Say what happened,
      // name the new status, and offer a way back. The server's resolved status
      // is authoritative because a publisher-written bucket lands on
      // "Handled by publisher" rather than the next workflow step.
      const nextStatus = res?.item?.status || res?.status || STATUS_FORWARD_MAP[prevStatus] || prevStatus;
      const stillVisible = statusParam === null || statusParam.includes(nextStatus);
      const title = item.story?.title || `Story ${String(item.storyId).slice(-6)}`;
      undoRef.current = { kind: 'status', id: item._id, prevStatus };
      // The daily pre-send cleanup archives suggestions that already went out,
      // and marking one now brings it back — otherwise it stayed archived and
      // In Production, which hides archived rows, never showed it. Say so: the
      // whole incident was a state change nobody was told about.
      const restored = res?.unarchived === true;
      const moved = `"${title}" moved to ${STATUS_LABELS[nextStatus] || nextStatus}`;
      setToast({
        message: restored
          ? `${moved} — restored from the archive.`
          : stillVisible
            ? `${moved}.`
            : `${moved} — no longer in this view.`,
        type: 'success',
        canUndo: true,
      });
      refreshAll();
    } catch {
      setToast({ message: 'Could not change the status.', type: 'error' });
    }
  }, [refreshAll, statusParam]);

  // §A2 — raise Suggestions to the top of the bucket. Works for one row from the
  // overflow menu and for a multi-selection from the bulk bar; a bulk move keeps
  // the selected items' relative order, so the ids are sent in DISPLAYED order.
  //
  // Deliberately NOT wired to "select all N matching": the action is about
  // putting specific stories where you can see them, and the server would have
  // to invent an order for rows the client never rendered.
  const handleMoveToTop = useCallback(async (items) => {
    const list = Array.isArray(items) ? items : [items];
    if (list.length === 0) return;
    const displayedOrder = displayedItemsRef.current;
    const rank = new Map(displayedOrder.map((it, i) => [it._id, i]));
    const ids = [...list]
      .sort((a, b) => (rank.get(a._id) ?? 0) - (rank.get(b._id) ?? 0))
      .map((it) => it._id);
    try {
      await api.moveBucketStoriesToTop(bucketId, ids, { silent: true });
      setToast({
        message: ids.length === 1 ? 'Moved to top.' : `${ids.length} moved to top.`,
        type: 'success',
      });
      setSelected(new Set());
      refreshAll();
    } catch {
      setToast({ message: 'Could not move to the top.', type: 'error' });
    }
  }, [bucketId, refreshAll]);

  // Adi #2 — same action In Production's row menu offers.
  const handleCopyStoryLink = useCallback(async (item) => {
    const title = item.story?.title || `Story ${String(item.storyId).slice(-6)}`;
    const url = item.story?.slug ? `${window.location.origin}/s/${item.story.slug}` : '';
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url ? `${title}\n${url}` : title);
      setToast({ message: 'Story title and link copied.', type: 'success' });
    } catch {
      setToast({ message: url ? `Could not copy — ${url}` : 'Could not copy the story link.', type: 'error' });
    }
  }, []);

  const handleReject = useCallback(async (item) => {
    try {
      await api.changeBucketStoryStatus(item._id, 'rejected', { silent: true });
      setToast({ message: 'Story rejected.', type: 'success' });
      refreshAll();
    } catch {
      setToast({ message: 'Could not reject the story.', type: 'error' });
    }
  }, [refreshAll]);

  const handleArchive = useCallback(async (item) => {
    try {
      await api.archiveBucketStory(item._id, { silent: true });
      undoRef.current = { kind: 'archive', ids: [item._id] };
      setToast({ message: 'Story archived.', type: 'success', canUndo: true });
      refreshAll();
    } catch {
      setToast({ message: 'Could not archive the story.', type: 'error' });
    }
  }, [refreshAll]);

  const handleUnarchive = useCallback(async (item) => {
    try {
      await api.unarchiveBucketStory(item._id, { silent: true });
      setToast({ message: 'Story unarchived.', type: 'success' });
      refreshAll();
    } catch {
      setToast({ message: 'Could not unarchive the story.', type: 'error' });
    }
  }, [refreshAll]);

  // Spec 6.7 — the row × is Delete: applied immediately with a temporary Undo.
  const handleDelete = useCallback(async (item) => {
    try {
      await api.removeBucketStory(item._id, { silent: true });
      undoRef.current = {
        kind: 'delete',
        storyId: item.storyId,
        bucketId,
        title: item.story?.title,
      };
      setToast({ message: 'Story deleted.', type: 'success', canUndo: true });
      refreshAll();
    } catch {
      setToast({ message: 'Could not delete the story.', type: 'error' });
    }
  }, [bucketId, refreshAll]);

  const handleUndo = useCallback(async () => {
    const ctx = undoRef.current;
    undoRef.current = null;
    setToast(null);
    if (!ctx) return;
    try {
      if (ctx.kind === 'archive') {
        if (ctx.ids.length > 1) await api.undoStaleArchive(ctx.ids, { silent: true });
        else for (const id of ctx.ids) await api.unarchiveBucketStory(id, { silent: true });
        setToast({ message: 'Archive undone.', type: 'success' });
      } else if (ctx.kind === 'status') {
        await api.changeBucketStoryStatus(ctx.id, ctx.prevStatus, { silent: true });
        setToast({ message: `Status restored to ${STATUS_LABELS[ctx.prevStatus] || ctx.prevStatus}.`, type: 'success' });
      } else if (ctx.kind === 'delete') {
        await api.addStoryToBucket(ctx.bucketId, ctx.storyId, null, { silent: true });
        setToast({ message: 'Story restored.', type: 'success' });
      } else if (ctx.kind === 'move') {
        // Recreate in the source, then remove the destination copy.
        await api.addStoryToBucket(ctx.sourceBucketId, ctx.storyId, null, { silent: true });
        if (ctx.createdBucketStoryId) {
          await api.removeBucketStory(ctx.createdBucketStoryId, { silent: true });
        }
        setToast({ message: 'Move undone.', type: 'success' });
      }
    } catch {
      setToast({ message: 'Could not undo that action.', type: 'error' });
    } finally {
      refreshAll();
    }
  }, [refreshAll]);

  // ── Copy / Move (spec 6.8) with duplicate handling (spec 5.2) ──
  const performTransfer = useCallback(async (items, destBucket, mode) => {
    const destClient = clients.find((c) => c.buckets.some((b) => b._id === destBucket._id));
    const destLabel = `${destClient?.clientName || ''} › ${destBucket.name}`;
    let done = 0;
    let blocked = 0;
    let excluded = 0;
    let lastResult = null;

    // The DESTINATION's exclusions. A transfer is the case that most needs this
    // and the one that had no check at all: the story was collected under the
    // SOURCE bucket's criteria, and nothing re-asked the question on arrival.
    //
    // Asked ONCE for the batch, on the first item, rather than per story. A
    // dialog per story in a ten-story drag is not a confirmation, it is an
    // obstacle people learn to click through, and the override it records is
    // the same decision for all of them. Stories the destination does not
    // refuse are unaffected either way: the server still checks each one.
    let override = false;
    const firstStoryId = items[0]?.storyId;
    if (firstStoryId) {
      const decision = await exclusion.request({
        bucketId: destBucket._id, storyId: firstStoryId, bucketName: destBucket.name,
      });
      if (!decision) return;
      override = decision.override;
    }

    for (const item of items) {
      const bucketStoryId = item.bucketStoryId || item._id;
      try {
        if (mode === 'move') {
          // Spec 6.8 — Move removes the source Suggestion.
          lastResult = await api.moveBucketStory(
            bucketStoryId, destBucket._id, undefined, { silent: true },
            { removeSource: true, overrideExclusion: override },
          );
        } else {
          lastResult = await api.copyBucketStory(
            bucketStoryId, destBucket._id, { silent: true }, { overrideExclusion: override },
          );
        }
        done += 1;
      } catch (err) {
        if (err?.response?.status === 409) blocked += 1;
        // 422 with this code is the destination refusing the story's language.
        // Counted separately from "already there" so the toast can say which
        // happened; a story refused on language is not a duplicate.
        else if (err?.response?.data?.code === 'EXCLUDED_LANGUAGE') excluded += 1;
      }
    }

    if (mode === 'move' && items.length === 1 && lastResult?.createdBucketStoryId) {
      undoRef.current = {
        kind: 'move',
        createdBucketStoryId: lastResult.createdBucketStoryId,
        sourceBucketId: bucketId,
        storyId: items[0].storyId,
      };
    } else {
      undoRef.current = null;
    }

    const notes = [
      blocked ? `${blocked} already there` : null,
      excluded ? `${excluded} refused by ${destBucket.name}` : null,
    ].filter(Boolean);
    setToast({
      message: done
        ? `${done} ${mode === 'move' ? 'moved' : 'copied'} to ${destLabel}${notes.length ? ` · ${notes.join(' · ')}` : ''}.`
        : 'Nothing was transferred.',
      type: done ? 'success' : 'error',
      canUndo: !!undoRef.current,
    });
    clearSelection();
    refreshAll();
  }, [bucketId, clients, refreshAll, exclusion]);

  // Runs the §5.2 duplicate gate for a single story before transferring.
  const transferWithDuplicateCheck = useCallback(async (items, destBucket, mode) => {
    if (items.length === 1) {
      try {
        const check = await api.checkBucketDuplicate(
          destBucket._id, items[0].storyId, { silent: true },
        );
        if (check && check.allowed === false) {
          setDuplicate({
            ...check.conflict,
            storyTitle: items[0].story?.title || items[0].title,
            pending: { items, destBucket, mode },
          });
          return;
        }
      } catch {
        /* a failed pre-check must not block the operation itself */
      }
    }
    performTransfer(items, destBucket, mode);
  }, [performTransfer]);

  // Spec 6.8 drag rules.
  const handleDropStory = useCallback((dragged, destBucket) => {
    const draggedIsSelected = selected.has(dragged.bucketStoryId);
    const batch = draggedIsSelected && selectedItems.length > 1 ? selectedItems : [dragged];
    const allSuggestions = batch.every((i) => i.status === 'suggestion');
    const destClient = clients.find((c) => c.buckets.some((b) => b._id === destBucket._id));
    const destinationName = `${destClient?.clientName || ''} › ${destBucket.name}`;

    if (allSuggestions) {
      setDragIntent({ count: batch.length, destinationName, batch, destBucket });
    } else {
      // Mixed or non-Suggestion → Copy only.
      transferWithDuplicateCheck(batch, destBucket, 'copy');
    }
  }, [selected, selectedItems, clients, transferWithDuplicateCheck]);

  // ── Newsletter creation (admin only) ──

  // The cap is enforced client-side too, so "select all matching" never fans
  // out hundreds of AI generations. Stories are taken in display order, which
  // is the bucket's own sortOrder-driven order (answer C1).
  /**
   * PRD §C3 — create a Media Analysis from this bucket.
   *
   * The capture is frozen server-side at the moment of the request, so what is
   * analysed is what the admin was looking at.
   *
   * Over the story cap the server answers 409 with the exact numbers instead of
   * truncating, and we ask for an explicit acknowledgement. §C3: "Do not
   * silently truncate."
   */
  const handleCreateMediaAnalysis = useCallback(async (confirmedOverflow) => {
    try {
      const res = await api.createMediaAnalysis(bucketId, confirmedOverflow, { silent: true });
      // §13 — do not promise a result the environment cannot produce. When the
      // worker is switched off the job is queued but nothing drains it, and the
      // old wording sent people to look in My Drafts for something that was
      // never going to appear.
      setToast(res?.warning
        ? { message: res.warning, type: 'warning' }
        : {
          message: res?.disclosure
            ? `Media analysis started. ${res.disclosure}`
            : 'Media analysis started — it will appear in My Drafts when ready.',
          type: 'success',
        });
    } catch (error) {
      const data = error?.response?.data;
      if (error?.response?.status === 409 && data?.code === 'MEDIA_ANALYSIS_OVER_CAP') {
        setMediaAnalysisOverflow({ total: data.total, cap: data.cap, message: data.message });
        return;
      }
      setToast({ message: data?.error || 'Could not start the media analysis.', type: 'error' });
    }
  }, [bucketId]);

  const openNewsletterFlow = useCallback(() => {
    const chosen = selectAllMatching
      ? stories.items
      : stories.items.filter((i) => selected.has(i._id));
    if (chosen.length === 0) return;

    const capped = chosen.slice(0, NEWSLETTER_MAX_STORIES);
    setNewsletterFlow({
      selection: capped.map((i) => ({
        bucketStoryId: i._id,
        title: i.story?.title || null,
      })),
      overflowCount: Math.max(0, chosen.length - NEWSLETTER_MAX_STORIES),
    });
  }, [selectAllMatching, stories.items, selected]);

  const onNewsletterCreated = useCallback((job) => {
    setNewsletterFlow(null);
    clearSelection();
    setNewsletterJob({ _id: job._id, status: job.status, total: job.items?.length || 0, completed: 0 });
  }, []);

  // Progress polling (answer F1). The job is server-side: navigating away or
  // closing the toast does not cancel it.
  useEffect(() => {
    if (!newsletterJob?._id) return undefined;
    if (newsletterJob.status === 'completed' || newsletterJob.status === 'failed') return undefined;

    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const next = await api.getNewsletterJob(newsletterJob._id, { silent: true });
        if (cancelled) return;
        setNewsletterJob(next);
      } catch {
        // A transient poll failure must not tear down the progress display.
      }
    }, 5000);

    return () => { cancelled = true; clearInterval(timer); };
  }, [newsletterJob?._id, newsletterJob?.status]);

  // ── Bulk actions (spec 6.7) ──
  const runBulk = useCallback(async (action) => {
    const selection = bulkSelection();
    try {
      if (action === 'archive') {
        await api.bulkArchiveScoped(bucketId, selection, true, { silent: true });
        setToast({ message: `${selectionCount} archived.`, type: 'success' });
      } else if (action === 'delete') {
        await api.bulkDeleteScoped(bucketId, selection, { silent: true });
        setToast({ message: `${selectionCount} deleted.`, type: 'success' });
      } else if (action === 'reject') {
        await api.bulkStatusScoped(bucketId, selection, 'rejected', { silent: true });
        setToast({ message: `${selectionCount} rejected.`, type: 'success' });
      }
      clearSelection();
      refreshAll();
    } catch {
      setToast({ message: 'The bulk action could not be completed.', type: 'error' });
    }
  }, [bucketId, selectionCount, refreshAll, bulkSelection]);

  // ── List actions (spec 6.10) ──
  const handleCopyList = useCallback(async () => {
    try {
      // Spec 6.10 — "Each action creates a fresh snapshot and establishes the
      // latest-list numbering basis." Copy list is not a passive read: the
      // numbers it copies must be the ones the client will see, so the capture
      // happens first and the export then reflects it.
      await api.createSuggestionList(bucketId, false, 'copy_list');
      const res = await api.exportBucketSuggestions(bucketId);
      const text = res?.text || '';
      if (text && navigator.clipboard) await navigator.clipboard.writeText(text);
      setToast({
        message: text ? `Copied ${res.count} suggestions.` : 'There are no suggestions to copy.',
        type: text ? 'success' : 'error',
      });
      refreshAll();
    } catch {
      setToast({ message: 'Could not copy the list.', type: 'error' });
    }
  }, [bucketId, refreshAll]);

  // Follow-up §2 — Copy link hands the URL straight to the client, so the
  // capture it creates must already ACCEPT responses. The public page gates on
  // `list.status === 'shared'` (PublicSuggestionsPage), and createSuggestionList
  // writes 'draft' unless autoShare is set — so passing false here produced a
  // link that opened on "no longer accepting responses". Every other surface
  // (BucketManager, BucketSidebar, admin BucketDetailPage) already passes true
  // for copy_link; this screen was the outlier.
  //
  // Send link deliberately still passes false: shareSuggestionList performs the
  // draft -> shared transition itself and rejects a list that is already shared.
  const handleCopyLink = useCallback(async () => {
    // §2.3 distinguishes two failures that must not be reported as one: the
    // list failing to be created, and the clipboard write failing afterwards.
    // Collapsing them said "Could not create the public link" for a list that
    // had in fact been created and shared — the administrator would recreate
    // it needlessly and never learn the URL. Clipboard writes fail for real
    // reasons (no focus, permission, insecure context), so the second case
    // surfaces the URL instead of swallowing it.
    let url;
    try {
      const res = await api.createSuggestionList(bucketId, true, 'copy_link');
      const token = res?.list?.publicToken;
      const status = res?.list?.status;
      if (!token) throw new Error('no token');
      // Never hand over a URL that would open a closed or unusable list.
      if (status && status !== 'shared') throw new Error(`list not active (${status})`);
      url = `${window.location.origin}/suggestions/${token}`;
    } catch {
      setToast({ message: 'Could not create the public link.', type: 'error' });
      return;
    }

    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setToast({ message: 'Public list link copied.', type: 'success' });
    } catch {
      // The list is live; only the copy failed. Show the URL so it is still usable.
      setToast({ message: `Link created but not copied — ${url}`, type: 'error' });
    }
    refreshAll();
  }, [bucketId, refreshAll]);

  // §1.11 — Selecting Send link opens a confirmation that names the exact
  // recipient. The actual send only fires from the confirmation, so the
  // administrator always sees who they are about to email.
  const openSendConfirm = useCallback(() => {
    if (!contact.canSend) return;
    setSendConfirm(true);
  }, [contact.canSend]);

  const confirmSendLink = useCallback(async () => {
    setSendConfirm(false);
    try {
      const created = await api.createSuggestionList(bucketId, false, 'send_link');
      const listId = created?.list?._id;
      const res = await api.sendBucketSuggestions(bucketId, listId, { silent: true });
      const names = (contact.recipients || []).map((r) => r.value).join(', ');
      setToast({ message: `Link sent to ${res?.recipient || names || 'the configured recipient'}.`, type: 'success' });
      refreshAll();
    } catch (err) {
      setToast({
        message: err?.response?.data?.error || 'The link could not be sent.',
        type: 'error',
      });
    }
  }, [bucketId, refreshAll, contact.recipients]);

  // §7.3 — one review open at a time; clicking the open row (or its chevron)
  // closes it, which is the already-working behaviour the chevron now advertises.
  const toggleExpandedStory = useCallback((id) => {
    setExpandedStoryId((prev) => (prev === id ? null : id));
  }, []);

  const toggleTargetClient = useCallback((clientId, forceOpen = false) => {
    setExpandedTargets((prev) => {
      const next = new Set(prev);
      if (forceOpen) next.add(clientId);
      else if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  // §10 — put the Target buckets accordion back the way it was before a search
  // auto-revealed matching publishers.
  const restoreExpandedTargets = useCallback((ids) => {
    setExpandedTargets(new Set(ids || []));
  }, []);

  // Spec 6.9 — beginning a Copy or Move automatically expands the panel.
  const openMoveModal = (items, mode) => {
    setTargetsCollapsed(false);
    setMoveModal({ items, mode });
  };

  if (!canEdit) return null;

  return (
    <div className="pbnew">
      <div className="pbnew-transfer">
        <div className="pbnew-main pbnew-main-head">
          <div className="pbnew-page-head">
            <div className="pbnew-head-titles">
              {/* Spec 6.1 / 2.6 — complete publisher and bucket names. */}
              <div className="pbnew-crumbs">
                <button className="pbnew-linkish" onClick={handleBack}>
                  Publisher Buckets
                </button>
                <span className="sep">›</span>
                <span>{client?.clientName || '—'}</span>
              </div>
              <h1 className="pbnew-h1">{bucket?.name || 'Bucket Stories'}</h1>
              {bucket?.targetLanguage && <span className="pbnew-lang">{bucket.targetLanguage}</span>}
              {/* Adi #9 — who writes for this bucket, us or the client.
                  It is a BUCKET-level setting (writingResponsibility, falling
                  back to the client default), not a per-story flag, so this is
                  the level it can honestly be shown at. The server already
                  resolves the inheritance into effectiveWritingResponsibility. */}
              {bucket?.effectiveWritingResponsibility && (
                <span
                  className={`pbnew-writing-tag ${bucket.effectiveWritingResponsibility === 'alchemiq' ? 'us' : 'client'}`}
                  title={bucket.effectiveWritingResponsibility === 'alchemiq'
                    ? 'Alchemiq writes the stories in this bucket'
                    : 'The client writes the stories in this bucket'}
                >
                  {bucket.effectiveWritingResponsibility === 'alchemiq' ? 'Written by Alchemiq' : 'Written by client'}
                </span>
              )}
            </div>
            <div className="pbnew-head-actions">
              <button className="pbnew-btn" onClick={handleBack}>
                <ArrowBackOutlinedIcon style={{ fontSize: 16 }} /> {backLabel}
              </button>
              {/* Adi #5 — from inside a bucket there was no way to reach In
                  Production without going back out to Publisher Buckets first. */}
              <button
                className="pbnew-btn"
                onClick={() => navigate('/publisher-buckets-new/in-production')}
                title="Alchemiq-written stories currently in production"
              >
                <PlayCircleOutlineOutlinedIcon style={{ fontSize: 16 }} /> In Production
              </button>
              <a
                className="pbnew-btn"
                href={bucket ? bucketConfigUrl(bucket._id) : '#'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SettingsOutlinedIcon style={{ fontSize: 16 }} /> Configure bucket
              </a>
              {/* §10 — the newsletter's name and style prompt are edited here,
                  not in the admin panel (answer G2). */}
              {isAdmin && (
                <button className="pbnew-btn" onClick={() => setNewsletterSettings(true)}>
                  <NewspaperOutlinedIcon style={{ fontSize: 15 }} /> Newsletter
                </button>
              )}
              {/* PRD §C3 — "Admin selects Create media analysis from the
                  bucket." Admin-only, like the newsletter flow: it freezes a
                  capture and then runs two heavy-model passes over it. */}
              {isAdmin && (
                <button className="pbnew-btn" onClick={() => handleCreateMediaAnalysis(false)}>
                  <InsightsOutlinedIcon style={{ fontSize: 15 }} /> Media analysis
                </button>
              )}
              <button className="pbnew-btn" onClick={handleCopyList}>
                <ContentCopyOutlinedIcon style={{ fontSize: 15 }} /> Copy list
              </button>
              <button className="pbnew-btn" onClick={handleCopyLink}>
                <LinkOutlinedIcon style={{ fontSize: 15 }} /> Copy link
              </button>
              <button
                className="pbnew-btn primary"
                onClick={openSendConfirm}
                disabled={!contact.canSend}
                title={contact.canSend ? 'Send the public list link' : contact.reason}
              >
                <SendOutlinedIcon style={{ fontSize: 15 }} /> Send link
              </button>
              {/* §1.9 — a menu, not a native <select>. The bucket-scoped variant. */}
              <ArchiveOldMenu
                onPick={(rule) => setStaleArchive({
                  rule, bucketId, scopeLabel: bucket?.name || 'this bucket',
                })}
              />
            </div>
          </div>
          {!contact.canSend && contact.reason && (
            <div className="pbnew-inline-note">{contact.reason}</div>
          )}

          {/* PRD item 5 — the operator notice. A persistent banner rather than
              a toast: the removal happened during a snapshot the editor may
              not have been watching, and the whole point is that a suggestion
              disappearing is explained rather than mysterious. "View affected
              suggestions" switches the archive filter, because removal IS an
              archive and the rows are recoverable from there. */}
          {languageNotice && (
            <div className="pbnew-lang-notice">
              <span>{languageNotice.message}</span>
              <button
                type="button"
                className="pbnew-linkish"
                // 'active' is the key; 'suggestion' is the STATUS it filters by.
                // Passing the status blanked the page: PRIMARY_FILTERS['suggestion']
                // is undefined and the lookup below reads .statuses on it.
                onClick={() => { setArchiveFilter('only'); setPrimary('active'); }}
              >
                View affected suggestions
              </button>
            </div>
          )}
        </div>

        <div className="pbnew-transfer-body">
          <div className="pbnew-transfer-main" ref={scrollRef}>
            {/* Spec 6.2 — primary filters and an INDEPENDENT archive filter. */}
            <div className="pbnew-tabs">
              {Object.entries(PRIMARY_FILTERS).map(([key, f]) => {
                const n = tabCount(key);
                return (
                  <button
                    key={key}
                    className={`pbnew-tab ${primary === key ? 'active' : ''}`}
                    onClick={() => setPrimary(key)}
                  >
                    {f.label}
                    {n !== null && <span className="badge">{n}</span>}
                  </button>
                );
              })}
              <div className="pbnew-tabs-right">
                {/* §A6 — archive is a flag, not a status, so it is asked
                    separately and combines with whichever tab is active. */}
                <select
                  className="pbnew-select"
                  value={archiveFilter}
                  onChange={(e) => setArchiveFilter(e.target.value)}
                  aria-label="Archive filter"
                >
                  {ARCHIVE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div className="pbnew-search" style={{ maxWidth: 240 }}>
                  <SearchOutlinedIcon style={{ fontSize: 16 }} />
                  <input placeholder="Search stories…" value={storySearch} onChange={(e) => setStorySearch(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="pbnew-selbar">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="pbnew-checkbox"
                  checked={stories.items.length > 0 && selected.size === stories.items.length}
                  onChange={toggleSelectAll}
                />
                Select all
              </label>
              {selected.size > 0 && (
                <>
                  <span>{selectionCount} selected</span>
                  {/* Spec 6.7 — Select all must be able to reach every matching
                      record, not just the rendered page. */}
                  {!selectAllMatching && selected.size === stories.items.length
                    && stories.total > stories.items.length && (
                    <button className="pbnew-linkish" onClick={() => setSelectAllMatching(true)}>
                      Select all {stories.total} matching
                    </button>
                  )}
                  <div className="pbnew-bulk-actions">
                    <button className="pbnew-btn" onClick={() => runBulk('archive')}>
                      <ArchiveOutlinedIcon style={{ fontSize: 15 }} /> Archive
                    </button>
                    <button className="pbnew-btn" onClick={() => runBulk('delete')}>
                      <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} /> Delete
                    </button>
                    <button className="pbnew-btn" onClick={() => runBulk('reject')}>
                      <BlockOutlinedIcon style={{ fontSize: 15 }} /> Reject
                    </button>
                    {/* §A2 — Suggestions only, and only for an explicit
                        selection: "all N matching" spans rows this client never
                        rendered, so there is no displayed order to preserve. */}
                    {!selectAllMatching && allSelectedAreSuggestions && (
                      <button className="pbnew-btn" onClick={() => handleMoveToTop(selectedItems)}>
                        <VerticalAlignTopOutlinedIcon style={{ fontSize: 15 }} /> Move to top
                      </button>
                    )}
                    {/* Follow-up §5 — Copy is the filled primary action in the
                        bulk flow, matching drag-and-drop and the overflow
                        menu. Move stays as a secondary button that opens the
                        selector already in Move mode; a user who explicitly
                        clicks Move meant Move. Copy remains the default when
                        the selector is opened via Copy to…. */}
                    <button className="pbnew-btn primary" onClick={() => openMoveModal(selectedItems, 'copy')}>
                      <ContentCopyOutlinedIcon style={{ fontSize: 15 }} /> Copy to…
                    </button>
                    {/* Move only when every selected source story is a Suggestion. */}
                    {canBulkMove && (
                      <button className="pbnew-btn" onClick={() => openMoveModal(selectedItems, 'move')}>
                        <DriveFileMoveOutlinedIcon style={{ fontSize: 15 }} /> Move to…
                      </button>
                    )}
                    {/* Newsletter creation is admin-only in v1 (answer D1).
                        Every status is eligible (SEL-01) and nothing about the
                        selection changes as a result (SEL-02). */}
                    {isAdmin && (
                      <button
                        className="pbnew-btn"
                        onClick={openNewsletterFlow}
                        data-testid="create-newsletter-button"
                      >
                        <NewspaperOutlinedIcon style={{ fontSize: 15 }} /> Create newsletter
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div>
              {loading && <BucketsLoading />}
              {!loading && stories.items.length === 0 && (
                <div className="pbnew-empty">No stories match this view.</div>
              )}
              {!loading && grouped.map((block) => (
                <div key={block.status || 'flat'}>
                  {block.status && (
                    <div className="pbnew-status-block">
                      {STATUS_LABELS[block.status] || block.status}
                      <span className="count">{block.items.length}</span>
                    </div>
                  )}
                  {block.items.map((item) => (
                    <StoryRow
                      key={item._id}
                      item={item}
                      index={stories.items.indexOf(item)}
                      checked={selected.has(item._id)}
                      onReorder={handleReorder}
                      onReorderCommit={handleReorderCommit}
                      onToggleCheck={toggleCheck}
                      onAdvanceStatus={handleAdvanceStatus}
                      onMoveTo={(it) => openMoveModal([it], 'move')}
                      onMoveToTop={(it) => handleMoveToTop(it)}
                      onCopyTo={(it) => openMoveModal([it], 'copy')}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onDelete={handleDelete}
                      onReject={handleReject}
                      onOpenComment={setComment}
                      onOpenFullStory={openFullStory}
                      onCopyStoryLink={handleCopyStoryLink}
                      expanded={expandedStoryId === item._id}
                      onToggleExpanded={toggleExpandedStory}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <TargetBucketsPanel
            clients={clients}
            loading={bucketsLoading}
            activeBucketId={bucketId}
            collapsed={targetsCollapsed}
            onToggleCollapsed={() => setTargetsCollapsed((v) => !v)}
            onDropStory={handleDropStory}
            onOpenBucket={(b) => navigate(`/publisher-buckets-new/bucket/${b._id}`)}
            expandedIds={expandedTargets}
            onToggleClient={toggleTargetClient}
            onRestoreExpanded={restoreExpandedTargets}
          />
        </div>
      </div>

      {moveModal && (
        <MoveToModal
          loading={bucketsLoading}
          isOpen
          mode={moveModal.mode}
          count={moveModal.items.length}
          incoming={moveModal.items}
          clients={clients}
          currentBucketId={bucketId}
          onSelect={(destBucket, chosenMode) => {
            const items = moveModal.items;
            setMoveModal(null);
            transferWithDuplicateCheck(items, destBucket, chosenMode);
          }}
          onClose={() => setMoveModal(null)}
        />
      )}

      <DragIntentModal
        intent={dragIntent}
        onChoose={(mode) => {
          const { batch, destBucket } = dragIntent;
          setDragIntent(null);
          transferWithDuplicateCheck(batch, destBucket, mode);
        }}
        onClose={() => setDragIntent(null)}
      />

      <DuplicateModal
        conflict={duplicate}
        onCopyAnyway={() => {
          const p = duplicate.pending;
          setDuplicate(null);
          performTransfer(p.items, p.destBucket, 'copy');
        }}
        onTransfer={() => {
          const p = duplicate.pending;
          setDuplicate(null);
          // Spec 5.2 — Transfer removes the existing Suggestion and adds it to
          // the target, i.e. a Move rather than a second appearance.
          performTransfer(p.items, p.destBucket, 'move');
        }}
        onClose={() => setDuplicate(null)}
      />

      {/* §1.11 — Send link confirmation names the exact recipient and address,
          so the administrator sees who they are about to email BEFORE the send
          fires. */}
      <AppModal
        isOpen={sendConfirm}
        title="Send link"
        onCancel={() => setSendConfirm(false)}
        onConfirm={confirmSendLink}
        confirmLabel="Send"
        maxWidth={460}
      >
        {/* §3.4 — show EVERY recipient's exact name and address before sending.
            Nothing is sent until Send is chosen, and there is deliberately no
            editing here: recipients are managed in Bucket administration. */}
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          The public list link will be emailed to
          {(contact.recipients || []).length === 1 ? ':' : ` these ${(contact.recipients || []).length} recipients:`}
        </p>
        {(contact.recipients || []).map((r) => (
          <div className="pbnew-recipient-card" key={r._id || r.value}>
            {r.name && <div className="pbnew-recipient-name">{r.name}</div>}
            <div className="pbnew-recipient-addr">{r.value}</div>
          </div>
        ))}
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#3C4A5E' }}>
          To change recipients, use <strong>Configure bucket → Settings → Endpoints</strong>.
        </p>
      </AppModal>

      <AppModal
        isOpen={!!comment}
        title="Client comment"
        onCancel={() => setComment(null)}
        cancelLabel="Close"
        maxWidth={460}
      >
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {comment?.clientFeedbackComment}
        </p>
      </AppModal>

      {/* PRD §C3 — over the cap, state the limit and require an explicit
          "continue with the first N". Never truncate behind the admin's back. */}
      {mediaAnalysisOverflow && (
        <AppModal
          isOpen
          title="Create media analysis"
          onCancel={() => setMediaAnalysisOverflow(null)}
          onConfirm={() => {
            const n = mediaAnalysisOverflow.cap;
            setMediaAnalysisOverflow(null);
            handleCreateMediaAnalysis(true);
            return n;
          }}
          confirmLabel={`Continue with first ${mediaAnalysisOverflow.cap}`}
          confirmVariant="primary"
          maxWidth={460}
        >
          <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
            This bucket has <strong>{mediaAnalysisOverflow.total}</strong> active suggestions, more
            than the <strong>{mediaAnalysisOverflow.cap}</strong> a media analysis covers.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
            {mediaAnalysisOverflow.message} The analysis will say so in its output, so it is never
            mistaken for a complete picture.
          </p>
        </AppModal>
      )}

      <StaleArchiveModal
        request={staleArchive}
        onClose={() => setStaleArchive(null)}
        onDone={(res) => {
          setStaleArchive(null);
          undoRef.current = { kind: 'archive', ids: res.archivedIds || [] };
          setToast({
            message: `${res.archivedCount} suggestion${res.archivedCount === 1 ? '' : 's'} archived.`,
            type: 'success',
            canUndo: (res.archivedIds || []).length > 0,
          });
          refreshAll();
        }}
      />

      {newsletterSettings && (
        <NewsletterSettingsModal
          bucketId={bucketId}
          bucketName={bucket?.name || 'This bucket'}
          onClose={() => setNewsletterSettings(false)}
        />
      )}

      {newsletterFlow && (
        <CreateNewsletterModal
          bucketId={bucketId}
          bucketName={bucket?.name || 'this bucket'}
          targetLanguage={bucket?.targetLanguage}
          selection={newsletterFlow.selection}
          overflowCount={newsletterFlow.overflowCount}
          maxStories={NEWSLETTER_MAX_STORIES}
          onCancel={() => setNewsletterFlow(null)}
          onCreated={onNewsletterCreated}
        />
      )}

      {/* Progress (answer F1). Deliberately not auto-dismissed while the job
          runs; the job itself is server-side and survives closing this. */}
      {newsletterJob && (
        <div className="pbnew-nl-progress" role="status" data-testid="newsletter-progress">
          {newsletterJob.status === 'completed' ? (
            <>
              <span>
                Newsletter ready
                {newsletterJob.failed > 0
                  ? ` — ${newsletterJob.failed} story/ies could not be generated and were left out.`
                  : '.'}
              </span>
              <button
                type="button"
                className="pbnew-toast-undo"
                onClick={() => navigate(`/my-drafts?expand=${newsletterJob.resultSavedBriefId}`)}
              >
                Open
              </button>
            </>
          ) : newsletterJob.status === 'failed' ? (
            <span>
              The newsletter could not be created
              {newsletterJob.failureDetails?.message ? `: ${newsletterJob.failureDetails.message}` : '.'}
            </span>
          ) : newsletterJob.status === 'assembling' ? (
            <span>Assembling the newsletter…</span>
          ) : (
            <span>
              Generating {(newsletterJob.completed || 0) + 1} of {newsletterJob.total}…
            </span>
          )}
          <button
            type="button"
            className="pbnew-toast-undo"
            onClick={() => setNewsletterJob(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={() => setToast(null)} />
      <ExclusionWarningModal {...exclusion.modalProps} />
    </div>
  );
}
